import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useSession } from '@/features/auth/hooks/useSession';
import { resolveAssistantName } from '@/lib/assistantName';
import { useConversation } from '@/app/providers/ConversationContext';
import { api } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import {
  getConversationChatSessions,
  getConversationChatMessages,
  type ChatSessionSummary,
  type ServerChatMessage,
} from '@/lib/api/endpoints/chat';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { fetchConversationSnapshot } from '@/features/conversations/lib/conversationAdapter';
import { renderConversationIcon } from './ConversationIcon';
import { renderModuleIcon, RestoreGlyph, DeleteGlyph } from './ArchivedModalIcons';
import './ArchivedModal.css';

interface ArchivedModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId?: string; // Canonical conversation scope
  archivedModules?: Module[]; // Optional: pass archived modules from parent
  archivedModulesLoading?: boolean; // Optional: show loading state while archived modules are being fetched
}

export const ArchivedModal: React.FC<ArchivedModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  archivedModules = [],
  archivedModulesLoading = false
}) => {
  const { token, profile, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const activeConversationId = conversationId;
  const { allConversations } = useConversation();
  const queryClient = useQueryClient();
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const [activeTab, setActiveTab] = useState<'conversations' | 'modules' | 'chats'>('conversations');
  const [archivedConversations, setArchivedConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Archived chats state
  const [archivedSessions, setArchivedSessions] = useState<ChatSessionSummary[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ChatSessionSummary | null>(null);
  const [sessionMessages, setSessionMessages] = useState<ServerChatMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Archived modules state (when fetched directly by this modal)
  const [fetchedArchivedModules, setFetchedArchivedModules] = useState<Module[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);

  useEscapeKey(onClose, isOpen);

  // Fetch archived conversations when the Conversations tab is active
  const fetchArchivedConversations = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const conversations = await api.workspaces.listConversations(token, workspaceId, { limit: 200, includeArchived: true });
      const archived = conversations
        .filter((conversation) => Boolean(conversation.archivedAt))
        .map((conversation) => ({
          id: conversation.id,
          name: conversation.title || 'New chat',
          icon: '📁',
          createdAt: conversation.createdAt,
          updatedAt: conversation.updatedAt,
          archivedAt: conversation.archivedAt,
          visibility: 'private',
        } as Conversation));
      setArchivedConversations(archived);
    } catch (err) {
      console.error('[ArchivedModal] Failed to fetch conversations:', err);
      setError('Failed to load archived conversations.');
    } finally {
      setIsLoading(false);
    }
  }, [token, workspaceId]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'conversations') return;
    void fetchArchivedConversations();
  }, [isOpen, activeTab, fetchArchivedConversations]);

  // Load archived sessions when Chats tab is active
  useEffect(() => {
    if (activeTab === 'chats' && isOpen) {
      loadArchivedSessions();
    }
  }, [activeTab, isOpen]);

  // Fetch archived modules function
  const fetchArchivedModules = useCallback(async () => {
    if (!activeConversationId || !token) return;
    setIsLoadingModules(true);
    try {
      const response = await fetchConversationSnapshot(token, workspaceId, activeConversationId, {
        includeArchivedModules: true,
      });
      const modules = response.conversation?.modules ?? [];
      const archived = modules.filter((mod: Module) => mod.archived);
      setFetchedArchivedModules(archived);
    } catch (err) {
      console.error('[ArchivedModal] Failed to fetch archived modules:', err);
    } finally {
      setIsLoadingModules(false);
    }
  }, [activeConversationId, token, workspaceId]);

  // Fetch archived modules when modal opens and a conversation scope is provided.
  // This happens immediately on open, not just when switching to Modules tab
  useEffect(() => {
    if (!isOpen || !activeConversationId || !token) return;
    // Only fetch if we don't already have modules passed as prop
    if (archivedModules.length > 0 || archivedModulesLoading) return;
    void fetchArchivedModules();
  }, [isOpen, activeConversationId, token, archivedModules.length, archivedModulesLoading, fetchArchivedModules]);

  const handleDeletePermanent = async (conversationToDeleteId: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to permanently delete this conversation? This action cannot be undone.')) {
      return;
    }
    setIsDeleting(true);
    try {
      await api.workspaces.deleteConversation(token, workspaceId, conversationToDeleteId, { permanent: true });
      await fetchArchivedConversations();
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    } catch (err) {
      console.error('[ArchivedModal] Failed to delete conversation:', err);
      setError('Failed to permanently delete conversation.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnarchive = async (moduleId: string) => {
    if (!activeConversationId || !token) return;
    try {
      setIsDeleting(true);
      await api.workspaces.restoreWorkspaceModule(token, workspaceId, moduleId);
      const panels = await api.workspaces.listConversationPanels(token, workspaceId, activeConversationId, { includeArchived: true });
      const panel = panels.find((entry) => entry.moduleId === moduleId);
      if (panel) {
        await api.workspaces.updateConversationPanel(token, workspaceId, activeConversationId, panel.id, { archived: false });
      } else {
        await api.workspaces.createConversationPanel(token, workspaceId, activeConversationId, {
          workspaceModuleId: moduleId,
          panelKey: `module:${moduleId}`,
          sortOrder: 0,
        });
      }
      // Refresh the archived modules list
      await fetchArchivedModules();
      // Invalidate queries to refresh active conversation views.
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    } catch (err) {
      console.error('[ArchivedModal] Failed to restore module:', err);
      setError('Failed to restore module.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteModule = async (_conversationId: string, moduleId: string) => {
    if (!token) return;
    try {
      setIsDeleting(true);
      await api.workspaces.deleteWorkspaceModule(token, workspaceId, moduleId, { permanent: true });
      // Refresh the archived modules list
      await fetchArchivedModules();
      // Invalidate queries to refresh active conversation views.
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    } catch (err) {
      console.error('[ArchivedModal] Failed to delete module:', err);
      setError('Failed to delete module.');
    } finally {
      setIsDeleting(false);
    }
  };

  const loadArchivedSessions = async () => {
    setIsLoadingSessions(true);
    try {
      // Fetch sessions from all conversations
      const allSessions: ChatSessionSummary[] = [];
      for (const conversation of allConversations) {
        const sessions = await getConversationChatSessions(token, conversation.id, { limit: 50 });
        allSessions.push(...sessions);
      }

      // Filter only archived sessions with messages and sort by most recent
      const archived = allSessions
        .filter(s => s.status === 'archived' && s.messageCount > 0)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      setArchivedSessions(archived);
    } catch (error) {
      console.error('Failed to load archived sessions:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadSessionMessages = async (session: ChatSessionSummary) => {
    setSelectedSession(session);
    setIsLoadingMessages(true);
    try {
      const { messages } = await getConversationChatMessages(token, session.conversationId, {
        sessionId: session.id,
        limit: 100,
        includeSummarized: true,
      });
      setSessionMessages(messages);
    } catch (error) {
      console.error('Failed to load session messages:', error);
      setSessionMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const closeSessionView = () => {
    setSelectedSession(null);
    setSessionMessages([]);
  };

  if (!isOpen) return null;

  const content = (
    <div className="archived-overlay" onMouseDown={handleOverlayMouseDown}
 onClick={handleOverlayClick}>
      <div ref={modalContentRef} className="archived-modal" onClick={(e) => e.stopPropagation()}>
        <div className="archived-header">
          <h2>View Archived</h2>
          <button className="archived-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="archived-tabs">
          <button
            className={`archived-tab-btn ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversations')}
          >
            Conversations
          </button>
          <button
            className={`archived-tab-btn ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('modules')}
          >
            Modules
          </button>
          <button
            className={`archived-tab-btn ${activeTab === 'chats' ? 'active' : ''}`}
            onClick={() => setActiveTab('chats')}
          >
            Chats
          </button>
        </div>

        <div className="archived-body">
          {activeTab === 'chats' ? (
            <div className="archived-section">
              {selectedSession ? (
                // Show messages for selected session
                <div className="session-detail-view">
                  <div className="session-detail-header">
                    <div className="session-header-row">
                      <button className="back-btn" onClick={closeSessionView}>
                        ← Back
                      </button>
                      <p className="session-meta">
                        {allConversations.find((conversation) => conversation.id === selectedSession.conversationId)?.name || 'Unknown Conversation'} • {selectedSession.messageCount} messages • {new Date(selectedSession.lastMessageAt || selectedSession.updatedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="session-messages">
                    {isLoadingMessages ? (
                      <p className="loading-text">Loading messages...</p>
                    ) : sessionMessages.length > 0 ? (
                      sessionMessages.map((msg) => (
                        <div key={msg.id} className={`archived-message ${msg.role}`}>
                          <div className="message-role">{msg.role === 'user' ? (profile?.name || 'User') : resolveAssistantName(profile)}</div>
                          <div className="message-content">{msg.content}</div>
                          <div className="message-time">{new Date(msg.createdAt).toLocaleString()}</div>
                        </div>
                      ))
                    ) : (
                      <p className="placeholder-text">No messages found</p>
                    )}
                  </div>
                </div>
              ) : (
                // Show list of archived sessions
                <>
                  {isLoadingSessions ? (
                    <p className="loading-text">Loading archived chats...</p>
                  ) : archivedSessions.length > 0 ? (
                    <div className="archived-sessions-list">
                      {archivedSessions.map((session) => {
                        const conversationName = allConversations.find((conversation) => conversation.id === session.conversationId)?.name || 'Unknown Conversation';
                        return (
                          <div
                            key={session.id}
                            className="archived-session-item"
                            onClick={() => loadSessionMessages(session)}
                          >
                            <div className="session-conversation">{conversationName}</div>
                            <div className="session-stats">
                              {session.messageCount} messages • {new Date(session.lastMessageAt || session.updatedAt).toLocaleDateString()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="placeholder-text">No archived chats yet</p>
                  )}
                </>
              )}
            </div>
          ) : activeTab === 'conversations' ? (
            <>
              {error && <div className="archived-error">{error}</div>}

              {isLoading ? (
                <div className="archived-empty">Loading archived conversations…</div>
              ) : archivedConversations.length === 0 ? (
                <div className="archived-empty">No archived conversations.</div>
              ) : (
                <div className="archived-list xv-scroll">
                  {archivedConversations.map((conversation) => {
                    const displayName = conversation.name.replace(/_archived_\d+$/, '');
                    return (
                    <div key={conversation.id} className="archived-row">
                      <div className="archived-meta">
                        <span className="archived-icon">{renderConversationIcon(conversation.icon)}</span>
                        <div>
                          <div className="archived-name">{displayName}</div>
                          <div className="archived-date">
                            Archived {conversation.archivedAt ? new Date(conversation.archivedAt).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                      <div className="archived-conversation-actions">
                        <button
                          type="button"
                          className="restore-conversation-btn"
                          onClick={async () => {
                            try {
                              await api.workspaces.updateConversation(token, workspaceId, conversation.id, { archived: false });
                              await fetchArchivedConversations();
                              queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
                            } catch (err) {
                              console.error('[ArchivedModal] Failed to restore conversation:', err);
                              setError('Failed to restore conversation.');
                            }
                          }}
                          disabled={isDeleting}
                          title="Restore conversation"
                        >
                          <RestoreGlyph />
                        </button>
                        <button
                          type="button"
                          className="delete-conversation-btn"
                          onClick={() => handleDeletePermanent(conversation.id)}
                          disabled={isDeleting}
                          title="Delete permanently"
                        >
                          <DeleteGlyph />
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {(() => {
                // Use prop modules if provided, otherwise use fetched modules
                const modulesToShow = archivedModules.length > 0 ? archivedModules : fetchedArchivedModules;
                const isLoading = archivedModulesLoading || isLoadingModules;

                // Find the current conversation to display its name and icon
                const currentConversation = activeConversationId
                  ? allConversations.find((conversation) => conversation.id === activeConversationId)
                  : null;

                if (isLoading) {
                  return <div className="archived-empty">Loading archived modules…</div>;
                }

                if (!activeConversationId) {
                  return <div className="archived-empty">Please select a conversation to view archived modules.</div>;
                }

                return (
                  <>
                    {/* Conversation Header */}
                    {currentConversation && (
                      <div className="archived-conversation-header">
                        <span className="conversation-icon">{renderConversationIcon(currentConversation.icon)}</span>
                        <span className="conversation-name">{currentConversation.name}</span>
                      </div>
                    )}

                    {modulesToShow.length === 0 ? (
                      <div className="archived-empty">No archived modules in this conversation.</div>
                    ) : (
                      <div className="archived-modules-grid xv-scroll">
                        {modulesToShow.map(module => (
                          <div
                            key={module.id}
                            className="archived-module-card"
                          >
                            <div className="module-content">
                              <div className="module-icon">
                                {renderModuleIcon(module.type)}
                              </div>
                              <div className="module-label">{module.label || module.type}</div>
                            </div>
                            <div className="archived-module-actions">
                              <button
                                className="restore-module-btn"
                                onClick={() => void handleUnarchive(module.id)}
                                disabled={isDeleting}
                                title="Restore module"
                              >
                                <RestoreGlyph />
                              </button>
                              <button
                                className="delete-module-btn"
                                onClick={async () => {
                                  if (window.confirm(`Permanently delete module "${module.label || module.type}"? This cannot be undone.`)) {
                                    if (activeConversationId) {
                                      await handleDeleteModule(activeConversationId, module.id);
                                    }
                                  }
                                }}
                                disabled={isDeleting}
                                title="Delete permanently"
                              >
                                <DeleteGlyph />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
